import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { noteService } from '../../services/api';
import api from '../../services/api';
import NoteDetailsModal from './NoteDetailsModal';
import FileLearningNote from '../learning/FileLearningNote';
import { toast } from 'react-hot-toast';
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  SparklesIcon,
  StarIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const statusConfig = {
  completed: { label: 'Analysed', tone: 'status-pill-success', icon: CheckCircleIcon },
  processing: { label: 'Processing', tone: 'status-pill-info', icon: ArrowPathIcon },
  failed: { label: 'Needs attention', tone: 'status-pill-danger', icon: ExclamationTriangleIcon },
  pending: { label: 'Pending review', tone: 'status-pill-warning', icon: ClockIcon },
  queued: { label: 'Review queued', tone: 'status-pill-info', icon: ClockIcon },
  disabled: { label: 'AI review off', tone: 'status-pill-info', icon: DocumentTextIcon },
};
const needsReview = note => !note.is_archived && (note.pending_review_count > 0 || ['pending', 'queued', 'processing', 'failed'].includes(note.processing_status));

const NotesList = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get('note');
  const linkedNote = useQuery({queryKey: ['note', linkedId], queryFn: () => noteService.getNote(Number(linkedId)), enabled: !!linkedId && /^\d+$/.test(linkedId), retry: false});
  const [selectedNote, setSelectedNote] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  useEffect(() => { if (linkedNote.data) {setSelectedNote(linkedNote.data);setShowDetails(true);} }, [linkedNote.data]);

  const { data: notes = [], isLoading, error, refetch } = useQuery({
    queryKey: ['notes'],
    queryFn: () => noteService.getNotes(),
    refetchInterval: query => query.state.data?.some(note => ['queued', 'processing'].includes(note.processing_status)) ? 3000 : false,
  });

  const visibleNotes = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return notes.filter((note) => {
      const matchesSearch = !normalized || `${note.title || ''} ${note.content || ''} ${(note.tags || []).join(' ')}`
        .toLowerCase()
        .includes(normalized);
      const matchesFilter =
        filter === 'all' ||
        (filter === 'pinned' && note.is_pinned) ||
        (filter === 'pending' && needsReview(note)) ||
        (filter === 'archived' && note.is_archived);
      return matchesSearch && matchesFilter;
    });
  }, [notes, query, filter]);

  const pendingCount = notes.filter(needsReview).length;

  const processMutation = useMutation({
    mutationFn: (noteId) => api.post(`/note-processing/process/${noteId}`).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      toast.success('Note sent for review');
    },
    onError: (processError) => toast.error(processError.response?.data?.detail || 'Could not process this note'),
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId) => noteService.deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes'] });
      toast.success('Note deleted');
    },
    onError: (deleteError) => toast.error(deleteError.response?.data?.detail || 'Could not delete this note'),
  });

  const openNote = (note) => {
    setSelectedNote(note);
    setShowDetails(true);
  };

  const deleteNote = (event, noteId) => {
    event.stopPropagation();
    if (window.confirm('Delete this note permanently?')) deleteMutation.mutate(noteId);
  };

  return (
    <div className="app-page">
      <div className="app-page-inner">
        {linkedNote.error && <p role="alert" className="text-sm text-red-700">This note is unavailable or was removed.</p>}
        <header className="page-header">
          <div>
            <div className="eyebrow">
              <DocumentTextIcon className="h-4 w-4" />
              Capture and context
            </div>
            <h1 className="page-title">Notes</h1>
            <p className="page-description">
              Keep raw ideas, meeting context, and future commitments in one searchable place. Review AI findings
              before turning them into calendar events.
            </p>
          </div>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('timelyzer:quick-note'))}
            className="primary-button"
          >
            <PlusIcon className="h-4 w-4" />
            Capture note
          </button>
        </header>

        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          {[
            ['All notes', notes.length, DocumentTextIcon, 'bg-[#e2edf6] text-[#32699a]'],
            ['Awaiting review', pendingCount, SparklesIcon, 'bg-[#f7edda] text-[#996617]'],
            ['Pinned', notes.filter((note) => note.is_pinned).length, StarIcon, 'bg-[#dcece4] text-[#246b52]'],
          ].map(([label, value, Icon, tone]) => (
            <div key={label} className="surface-panel flex items-center gap-3 p-4">
              <span className={`flex h-10 w-10 items-center justify-center rounded-md ${tone}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xl font-bold">{value}</p>
                <p className="text-xs font-semibold text-[#758078]">{label}</p>
              </div>
            </div>
          ))}
        </div>

        <section className="surface-panel overflow-hidden">
          <div className="flex flex-col flex-wrap gap-3 border-b border-[#e2e7e3] p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:min-w-[160px] md:flex-1 md:max-w-md">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-3 h-5 w-5 text-[#8a948e]" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="field-input pl-10"
                placeholder="Search titles, notes, or tags"
                aria-label="Search notes"
              />
            </div>
            <div className="flex max-w-full shrink-0 overflow-x-auto rounded-md border border-[#d8dfda] bg-[#f5f7f5] p-0.5">
              {[
                ['all', 'All'],
                ['pending', 'Review'],
                ['pinned', 'Pinned'],
                ['archived', 'Archived'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setFilter(id)}
                  className={`min-h-9 whitespace-nowrap rounded px-3 text-xs font-bold ${
                    filter === id ? 'bg-white text-[#1e6049] shadow-sm' : 'text-[#6b766f] hover:text-[#17211c]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-1 p-3">
              {[1, 2, 3, 4].map((item) => (
                <div key={item} className="h-24 animate-pulse rounded-md bg-[#eef1ee]" />
              ))}
            </div>
          ) : error ? (
            <div className="px-6 py-16 text-center">
              <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-[#c65f43]" />
              <h2 className="mt-3 text-base font-bold">Notes unavailable</h2>
              <p className="mt-2 text-sm text-[#6b766f]">We could not load your notes.</p>
              <button onClick={() => refetch()} className="secondary-button mt-4">Try again</button>
            </div>
          ) : visibleNotes.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-[#e4eee8] text-[#246b52]">
                <DocumentTextIcon className="h-6 w-6" />
              </span>
              <h2 className="mt-4 text-base font-bold">{query || filter !== 'all' ? 'No matching notes' : 'Capture your first thought'}</h2>
              <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[#6b766f]">
                {query || filter !== 'all'
                  ? 'Adjust your search or filter to see more notes.'
                  : 'Write naturally. Timelyzer can review it for commitments and keep the original context intact.'}
              </p>
              {!query && filter === 'all' && (
                <button onClick={() => window.dispatchEvent(new CustomEvent('timelyzer:quick-note'))} className="primary-button mt-5">
                  <PlusIcon className="h-4 w-4" />
                  Capture note
                </button>
              )}
            </div>
          ) : (
            <div>
              {visibleNotes.map((note) => {
                const config = statusConfig[note.processing_status] || statusConfig.pending;
                const StatusIcon = config.icon;
                return (
                  <article
                    key={note.id}
                    onClick={() => openNote(note)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') openNote(note);
                    }}
                    role="button"
                    tabIndex={0}
                    className="group grid cursor-pointer gap-3 border-b border-[#e9edea] px-4 py-4 transition hover:bg-[#f8faf8] last:border-0 md:grid-cols-[minmax(0,1fr)_180px_92px] md:items-center"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {note.is_pinned && <StarIcon className="h-4 w-4 fill-[#d6a64c] text-[#d6a64c]" />}
                        <h2 className="truncate text-sm font-bold">{note.title || 'Untitled note'}</h2>
                      </div>
                      <p className="mt-1 line-clamp-2 text-sm leading-6 text-[#66716a]">{note.content}</p>
                      <FileLearningNote note={note} />
                      {note.tags?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {note.tags.slice(0, 3).map((tag) => (
                            <span key={tag} className="rounded bg-[#edf1ed] px-2 py-1 text-[10px] font-bold text-[#637068]">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between gap-3 md:block">
                      <span className={`status-pill ${config.tone}`}>
                        <StatusIcon className={`h-3.5 w-3.5 ${note.processing_status === 'processing' ? 'animate-spin' : ''}`} />
                        {note.pending_review_count > 0 ? `${note.pending_review_count} to review` : config.label}
                      </span>
                      <p className="text-xs text-[#8a948e] md:mt-2">
                        {format(new Date(note.updated_at || note.created_at), 'MMM d, yyyy')}
                      </p>
                    </div>

                    <div className="flex items-center justify-end gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
                      {['pending', 'failed'].includes(note.processing_status) && (
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            processMutation.mutate(note.id);
                          }}
                          className="icon-button"
                          title="Review with AI"
                          aria-label="Review with AI"
                        >
                          <SparklesIcon className="h-4 w-4" />
                        </button>
                      )}
                      {note.is_archived && (
                        <span className="icon-button" title="Archived">
                          <ArchiveBoxIcon className="h-4 w-4" />
                        </span>
                      )}
                      <button
                        onClick={(event) => deleteNote(event, note.id)}
                        className="icon-button hover:border-[#e6c4ba] hover:bg-[#fff7f5] hover:text-[#a94d35]"
                        title="Delete note"
                        aria-label="Delete note"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <NoteDetailsModal
        note={selectedNote}
        open={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedNote(null);
          if (linkedId) {const next = new URLSearchParams(searchParams);next.delete('note');setSearchParams(next,{replace:true});}
        }}
        onUpdate={() => queryClient.invalidateQueries({ queryKey: ['notes'] })}
      />
    </div>
  );
};

export default NotesList;
